import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { BrandLogo } from "./BrandLogo";
import { ThemeToggle } from "./ThemeToggle";
import { Footer } from "./Footer";
import { useSmoothScroll } from "../hooks/useSmoothScroll";

export const LegalLayout = ({ overline, title, titleTestId, children }) => {
  useSmoothScroll();

  return (
    <div className="relative min-h-screen bg-bg text-ink">
      <div className="noise-overlay" />

      <header className="fixed inset-x-0 top-0 z-50 flex justify-center px-4 pt-4">
        <nav className="flex w-full max-w-4xl items-center justify-between rounded-full border border-line bg-surface/80 px-4 py-2.5 pl-5 shadow-soft backdrop-blur-xl">
          <Link to="/" data-testid="legal-logo" aria-label="BREELINK home" className="flex items-center">
            <BrandLogo eager className="h-6 sm:h-7" />
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              data-testid="legal-back-home"
              className="strike-link hidden items-center gap-1.5 text-sm font-semibold text-ink sm:inline-flex"
            >
              <ArrowLeft size={15} /> Back to home
            </Link>
            <ThemeToggle testid="legal-theme-toggle" />
          </div>
        </nav>
      </header>

      <main className="relative z-10 mx-auto max-w-3xl px-6 pb-24 pt-36 md:pt-44">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-xs font-semibold uppercase tracking-[0.25em] text-inkMuted"
        >
          {overline}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          data-testid={titleTestId}
          className="mt-4 font-display text-4xl font-semibold tracking-tighter text-ink md:text-6xl"
        >
          {title}
          <span className="mt-3 block h-[5px] w-24 bg-lime" />
        </motion.h1>

        {children}
      </main>

      <Footer />
    </div>
  );
};

export const LegalSection = ({ title, children, testid }) => (
  <section data-testid={testid} className="mt-11">
    <h2 className="font-display text-xl font-semibold tracking-tight text-ink md:text-2xl">
      {title}
      <span className="mt-2 block h-[2px] w-10 bg-lime" />
    </h2>
    <div className="mt-5 space-y-4 text-sm leading-relaxed text-inkMuted md:text-base">{children}</div>
  </section>
);

export const LegalList = ({ items }) => (
  <ul className="space-y-3">
    {items.map((item, i) => (
      <li key={i} className="flex gap-3">
        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lime" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export const LegalSubhead = ({ children }) => (
  <p className="pt-1 text-sm font-semibold text-ink md:text-base">{children}</p>
);
